import { useCallback, useEffect, useState } from "react";
import {
  getViewerActions,
  getViewerState,
  useViewerActions,
  useViewerStore,
} from "../stores/viewerStore";
import {
  DEFAULT_JOINT_VISIBILITY_UI_STATE,
  getJointVisibilityUiState,
  setJointVisibilityUiState,
  type JointVisibilityUiState,
} from "./visibilityEvents";
import { dispatchJointPositionSliderVisibility } from "./positionSliderEvents";

export type JointVisibilityState = JointVisibilityUiState;

const defaultState = (): JointVisibilityState => ({
  ...DEFAULT_JOINT_VISIBILITY_UI_STATE,
});

let rememberedJointVisibilityState: JointVisibilityState = defaultState();

export type UseJointVisibilityControlsArgs = {
  jointUuid: string | null;
  bodyUuid: string | null;
  enabled?: boolean;
  initialState?: Partial<JointVisibilityState>;
  onStateChange?: (state: JointVisibilityState) => void;
};

export type UseJointVisibilityControlsResult = {
  state: JointVisibilityState;
  disabled: boolean;
  focusDisabled: boolean;
  toggleFocus: () => void;
  toggleFrameAxes: () => void;
  togglePositionSlider: () => void;
  reset: () => void;
};

export function useJointVisibilityControls({
  jointUuid,
  bodyUuid: _bodyUuid,
  enabled = true,
  initialState,
  onStateChange,
}: UseJointVisibilityControlsArgs): UseJointVisibilityControlsResult {
  const showJoints = useViewerStore((s) => s.showJoints);
  const viewerActions = useViewerActions();
  const [state, setStateInternal] = useState<JointVisibilityState>(() => {
    const persisted = getViewerState().jointInspectorVisibility;
    return {
      ...defaultState(),
      ...rememberedJointVisibilityState,
      ...persisted,
      labelsOff: getJointVisibilityUiState().labelsOff,
      ...initialState,
    };
  });

  const disabled = !enabled || !jointUuid;
  const focusDisabled = disabled;

  const setState = useCallback(
    (patch: Partial<JointVisibilityState>) => {
      setStateInternal((prev) => {
        const next = { ...prev, ...patch };
        rememberedJointVisibilityState = next;
        onStateChange?.(next);
        return next;
      });
    },
    [onStateChange]
  );

  const ensureJointVisibilityOn = useCallback(() => {
    if (!showJoints) {
      viewerActions.toggleShowJoints();
    }
  }, [showJoints, viewerActions]);

  useEffect(() => {
    setJointVisibilityUiState({
      focus: state.focus,
      frameAxes: state.frameAxes,
      positionSlider: state.positionSlider,
    });
    dispatchJointPositionSliderVisibility(state.positionSlider);
    getViewerActions().setJointInspectorVisibility({
      positionSlider: state.positionSlider,
    });
  }, [state.focus, state.frameAxes, state.positionSlider]);

  useEffect(() => {
    return () => {
      const persisted = getViewerState().jointInspectorVisibility;
      setJointVisibilityUiState({
        focus: false,
        frameAxes: false,
        positionSlider: persisted.positionSlider,
      });
      dispatchJointPositionSliderVisibility(false);
    };
  }, []);

  const toggleFocus = useCallback(() => {
    if (focusDisabled) return;
    const next = !state.focus;
    if (next) ensureJointVisibilityOn();
    setState({ focus: next });
  }, [focusDisabled, ensureJointVisibilityOn, setState, state.focus]);

  const toggleFrameAxes = useCallback(() => {
    if (disabled) return;
    const next = !state.frameAxes;
    if (next) ensureJointVisibilityOn();
    setState({ frameAxes: next });
  }, [disabled, ensureJointVisibilityOn, setState, state.frameAxes]);

  const togglePositionSlider = useCallback(() => {
    if (disabled) return;
    const next = !state.positionSlider;
    if (next) ensureJointVisibilityOn();
    setState({ positionSlider: next });
  }, [disabled, ensureJointVisibilityOn, setState, state.positionSlider]);

  const reset = useCallback(() => {
    setJointVisibilityUiState(DEFAULT_JOINT_VISIBILITY_UI_STATE);
    dispatchJointPositionSliderVisibility(false);
    getViewerActions().setJointInspectorVisibility({ labelsOff: true, positionSlider: false });
    setStateInternal(() => {
      const next = { ...defaultState(), ...initialState };
      rememberedJointVisibilityState = next;
      onStateChange?.(next);
      return next;
    });
  }, [initialState, onStateChange]);

  return {
    state,
    disabled,
    focusDisabled,
    toggleFocus,
    toggleFrameAxes,
    togglePositionSlider,
    reset,
  };
}
